import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, type InstanceCustomisationView } from "@keydials/api-client";
import type { SegmentNoun } from "@keydials/dial-workspace";
import { api, getAccessToken } from "../auth/AuthContext";
import { REVEAL_MINUTES, hiddenPartsSummary, revealBannerText, revealRemainingMs, structureSettingsPath } from "./hiddenParts";

const NOTE_STYLE = {
  display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, margin: "0 0 12px", padding: "9px 12px",
  borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)", background: "var(--surface-2)",
  fontSize: 13.5, color: "var(--text-muted)",
} as const;

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError && e.message ? e.message : fallback);

/**
 * KEY-737 → KEY-754: the dial admin's reminder, on the Overview, that parts of this dial are hidden.
 *
 * Hidden means hidden from everyone, the admin included — so a child holding the phone doesn't see Christmas.
 * Only someone who may unhide (`canCustomise`) is told anything; everyone else gets nothing at all, not even a
 * count (see {@link hiddenPartsSummary}). The admin can open Settings → Structure to unhide for good, or have the
 * hidden parts shown to them alone for {@link REVEAL_MINUTES} minutes, which the server honours on their own reads
 * only. While that is running the {@link HiddenRevealBanner} takes over, so the offer isn't repeated here.
 */
export function HiddenSectionsNote({ instanceId, view, noun, revealing, onChanged }: {
  instanceId: string;
  view: InstanceCustomisationView | null;
  noun: SegmentNoun;
  /** A reveal is already running for this person (`detail.hiddenRevealExpiresAtUtc`). */
  revealing: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const summary = hiddenPartsSummary(view, noun);
  if (!summary) return null;

  const reveal = async () => {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.revealHiddenParts(token, instanceId, REVEAL_MINUTES);
      onChanged();
    } catch (e) {
      setError(errorText(e, "Couldn't show them just now."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="note" style={NOTE_STYLE}>
      <span style={{ flex: "1 1 200px" }}>
        {summary.headline}: <strong style={{ color: "var(--text)" }}>{summary.names.join(", ")}</strong>. Nobody
        on this dial can see {summary.names.length === 1 ? "it" : "them"}, you included.{" "}
        <Link to={structureSettingsPath(instanceId)}>Structure settings</Link>
      </span>
      {!revealing && (
        <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void reveal()}>
          Show to me for {REVEAL_MINUTES} minutes
        </button>
      )}
      {error && <span role="status">{error}</span>}
    </div>
  );
}

/**
 * KEY-754: the standing banner while a dial admin is looking at the parts they hid — "Showing hidden parts to you
 * until 14:32", with "Hide again". Re-reads the dial the moment the reveal lapses, so the hidden parts go away on
 * screen when the server stops sending them, not on the next refresh.
 */
export function HiddenRevealBanner({ instanceId, expiresAtUtc, onChanged }: {
  instanceId: string;
  expiresAtUtc: string | null | undefined;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ms = revealRemainingMs(expiresAtUtc);
    if (ms === null) return;
    // +1s so the read lands after the server's own clock has passed the expiry. setTimeout caps at ~24.8 days;
    // a reveal is at most an hour.
    const timer = window.setTimeout(onChanged, ms + 1000);
    return () => window.clearTimeout(timer);
  }, [expiresAtUtc, onChanged]);

  const text = revealBannerText(expiresAtUtc);
  if (!text) return null;

  const hideAgain = async () => {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.endHiddenReveal(token, instanceId);
      onChanged();
    } catch (e) {
      setError(errorText(e, "Couldn't hide them just now."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="status" aria-label="Hidden parts shown to you" style={{ ...NOTE_STYLE, borderStyle: "dashed" }}>
      <span style={{ flex: "1 1 200px" }}>
        <strong style={{ color: "var(--text)" }}>{text}.</strong> Parts marked Hidden are visible only to you.
      </span>
      <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void hideAgain()}>
        Hide again
      </button>
      {error && <span>{error}</span>}
    </div>
  );
}
